import {
	type CSSProperties,
	type FC,
	forwardRef,
	type HTMLAttributes,
	type ReactNode
} from 'react'
import { AnswerItem } from '@/components/commons/answer-item'
import type { InputTextProps } from '@/components/commons/input-text'
import { InputText } from '@/components/commons/input-text'
import { useTranslation } from '@/translations/i18n'

export const Footer = ({
	justify = 'space-between',
	className,
	...rest
}: HTMLAttributes<HTMLDivElement> & {
	justify?: CSSProperties['justifyContent']
}) => (
	<footer
		className={`flex items-center border-neutral-300 border-t p-4 ${className ?? ''}`}
		style={{ justifyContent: justify }}
		{...rest}
	/>
)

export const Title = ({
	className,
	...props
}: HTMLAttributes<HTMLHeadElement>) => (
	<h1 className={`font-medium text-2xl ${className ?? ''}`} {...props} />
)

export const SectionDescription = ({
	className,
	...props
}: HTMLAttributes<HTMLParagraphElement>) => (
	<p className={`text-neutral-700 text-sm ${className ?? ''}`} {...props} />
)

export const Content = ({
	children,
	gap,
	title,
	fullWidth,
	titleEndSlot,
	className,
	...rest
}: HTMLAttributes<HTMLDivElement> & {
	gap?: number
	title: string
	fullWidth?: boolean
	titleEndSlot?: ReactNode
}) => (
	<main className={`h-full overflow-auto p-8 ${className ?? ''}`} {...rest}>
		<div
			className='mx-auto flex max-w-full flex-col'
			style={{
				gap: `${(gap ?? 12) * 4}px`,
				width: fullWidth ? '100%' : '600px'
			}}
		>
			<div className='flex items-center justify-between'>
				<Title>{title}</Title>
				{titleEndSlot}
			</div>
			{children}
		</div>
	</main>
)

export const ColumnFields = ({
	className,
	...props
}: HTMLAttributes<HTMLDivElement>) => (
	<div
		className={`grid w-full grid-cols-2 gap-4 ${className ?? ''}`}
		{...props}
	/>
)

interface YesNoFieldProps {
	direction?: 'row' | 'column'
	labelTitle?: ReactNode
	labelDescription?: ReactNode
	name?: string
	value?: boolean
	onChange: (value: boolean) => void
}
export const YesNoField: FC<YesNoFieldProps> = ({
	direction,
	labelTitle,
	labelDescription,
	name,
	onChange,
	value
}) => {
	const { t } = useTranslation()
	return (
		<div
			className={`flex w-full gap-3 ${
				direction === 'column'
					? 'flex-col items-start'
					: 'flex-row items-center justify-between'
			}`}
		>
			<div className='flex flex-col gap-1.5'>
				{labelTitle && (
					<h4 className='text-base [&_b]:font-medium'>
						{labelTitle}
					</h4>
				)}
				{labelDescription && (
					<p className='text-neutral-700 text-sm [&_b]:font-medium'>
						{labelDescription}
					</p>
				)}
			</div>
			<div className='flex gap-3'>
				<AnswerItem
					checked={value}
					className='w-[60px] text-center'
					inputSize='sm'
					label={t('experience_shell:yes')}
					name={name}
					onChange={() => onChange(true)}
					type='radio'
					value='yes'
				/>
				<AnswerItem
					checked={value !== undefined && !value}
					className='w-[60px] text-center'
					inputSize='sm'
					label={t('experience_shell:no')}
					name={name}
					onChange={() => onChange(false)}
					type='radio'
					value='no'
				/>
			</div>
		</div>
	)
}

interface FieldsetProps {
	title?: string
	children: ReactNode
	gap?: number
	contentGap?: number
}
export const Fieldset: FC<FieldsetProps> = ({
	title,
	gap = 5,
	contentGap = 4,
	children
}) => (
	<div className='flex w-full flex-col' style={{ gap: `${gap * 4}px` }}>
		{!!title && <FieldTitle>{title}</FieldTitle>}
		<div
			className='flex w-full flex-col'
			style={{ gap: `${contentGap * 4}px` }}
		>
			{children}
		</div>
	</div>
)

export const FieldTitle = ({
	className,
	...props
}: HTMLAttributes<HTMLHeadingElement>) => (
	<h3
		className={`font-medium text-base [&_a]:cursor-pointer [&_a]:underline ${className ?? ''}`}
		{...props}
	/>
)

export const InputPrice = forwardRef<
	HTMLInputElement,
	Omit<InputTextProps, 'type'>
>((props, ref) => (
	<InputText
		className='w-full [&_div]:gap-2.5 [&_input::-webkit-inner-spin-button]:m-0 [&_input::-webkit-inner-spin-button]:appearance-none [&_input::-webkit-outer-spin-button]:m-0 [&_input::-webkit-outer-spin-button]:appearance-none [&_input[type=number]]:[-moz-appearance:textfield]'
		inputMode='decimal'
		inputSize='sm'
		onKeyDown={(evt) =>
			['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()
		}
		step='0.01'
		type='number'
		typeIcon={<span>USD</span>}
		{...props}
		ref={ref}
	/>
))
